/**
 * Pure linked-context helpers, split out of `LinkContextButton.tsx` so
 * non-component consumers (hooks, the transport, the seeded-first-turn flow)
 * can use them WITHOUT pulling in the button's heavy transitive deps
 * (`LinkContextButton.parts` → source-control popover → auth/store). Importing a
 * value from the component module drags that whole chain in, which triggers a
 * circular-init `ReferenceError` when loaded from a light import graph.
 */

/**
 * Discriminated union for one linkage. Carries display fields so
 * chips + the serialised preamble don't need a second lookup. The
 * agent already has tools to fetch detail; the ids are what matter
 * for that, the labels are for the user.
 */
export type LinkedContextItem =
  | {
      type: 'session';
      sessionId: string;
      sessionName: string;
      patientName?: string;
      avatarSeed?: string;
      duration?: string;
      dateLabel?: string;
      /** Account-timezone-aware session time used by compact picker rows. */
      createdAt?: number;
      sessionTitle?: string;
      /**
       * Inline `@` token label: the session's name with its day, else its date-time.
       * Not `sessionName`, which is a placeholder for an untitled session.
       */
      tokenLabel?: string;
    }
  | {
      type: 'patient';
      patientName: string;
      avatarSeed?: string;
      sexAge?: string;
      lastSeen?: string;
      /**
       * Real ``patient_profile_id``, read off the session's linked
       * ``patient_profile`` (with a name match as a secondary
       * fallback). Optional because a session may not be linked to a
       * profile yet; when absent the agent fetches by name and the
       * canvas opens nothing rather than mis-routing to a session.
       */
      patientId?: string;
    }
  | {
      type: 'library_document';
      documentId: string;
      filename: string;
      documentKind: 'artifact' | 'workflow_context' | 'input';
    }
  | {
      type: 'template';
      templateId: string;
      label: string;
      /** True for a PDF form template; emits ``@form_template:`` on the wire. */
      formTemplate?: boolean;
    }
  | {
      type: 'task';
      taskId: string;
      label: string;
      sessionId?: string;
      patientName?: string;
      /** ``YYYY-MM-DD``, as the task stores it. */
      dueDate?: string;
      status?: 'todo' | 'in_progress';
    }
  | { type: 'collection'; collectionId: string; label: string; documentCount?: number };

/**
 * Serialise the linked items into a context preamble that the
 * composer prepends to the user's prompt before calling
 * ``onSubmit``. The agent reads this verbatim in its first turn so
 * a "use this session" / "this patient" intent is preserved into
 * the durable thread.
 *
 * Format is deliberately compact and matches what the orchestrator
 * already recognises in its system prompt (each line "@<type>: …"
 * is treated as auxiliary context rather than user instruction).
 */
export const serializeLinkedContext = (items: readonly LinkedContextItem[]): string => {
  if (items.length === 0) return '';
  const lines = items.map((item) => {
    if (item.type === 'session') {
      const patient = item.patientName ? ` (${item.patientName})` : '';
      return `@session: ${item.sessionName}${patient} [id=${item.sessionId}]`;
    }
    if (item.type === 'library_document') {
      const kind =
        item.documentKind === 'workflow_context'
          ? 'skill_file'
          : item.documentKind === 'input'
            ? 'upload'
            : 'work_output';
      return `@${kind}: ${item.filename} [id=${item.documentId}]`;
    }
    if (item.type === 'template') {
      const prefix = item.formTemplate ? '@form_template:' : '@template:';
      return `${prefix} ${item.label} [id=${item.templateId}]`;
    }
    if (item.type === 'task') return `@task: ${item.label} [id=${item.taskId}]`;
    if (item.type === 'collection')
      return `@collection: ${item.label} [id=${item.collectionId}]`;
    const id = item.patientId ? ` [id=${item.patientId}]` : '';
    return `@patient: ${item.patientName}${id}`;
  });
  return `${lines.join('\n')}\n\n`;
};

export const linkedContextItemKey = (item: LinkedContextItem): string => {
  if (item.type === 'session') return `session:${item.sessionId}`;
  if (item.type === 'library_document') return `library:${item.documentId}`;
  if (item.type === 'template') return `template:${item.templateId}`;
  if (item.type === 'task') return `task:${item.taskId}`;
  if (item.type === 'collection') return `collection:${item.collectionId}`;
  // Two different patients can share a name; only an id-less patient falls
  // back to the name, or the second one's id never reaches the preamble.
  if (item.patientId) return `patient:${item.patientId}`;
  return `patient-name:${item.patientName.toLowerCase()}`;
};

export const linkedContextItemLabel = (item: LinkedContextItem): string => {
  if (item.type === 'session') return item.sessionName;
  if (item.type === 'library_document') return item.filename;
  if (item.type === 'template' || item.type === 'task' || item.type === 'collection')
    return item.label;
  return item.patientName;
};

export const linkedContextItemSubtitle = (
  item: LinkedContextItem
): string | undefined => {
  if (item.type === 'session' || item.type === 'task') return item.patientName;
  if (item.type === 'library_document') {
    if (item.documentKind === 'workflow_context') return 'Skill file';
    if (item.documentKind === 'input') return 'Upload';
    return 'Heidi output';
  }
  return undefined;
};

/** True when two linked-context chips refer to the same session or patient. */
export const isSameLinkedContext = (
  a: LinkedContextItem | undefined,
  b: LinkedContextItem | undefined
): boolean => {
  if (!a || !b || a.type !== b.type) return false;
  if (a.type === 'session' && b.type === 'session') return a.sessionId === b.sessionId;
  if (a.type === 'patient' && b.type === 'patient')
    return a.patientId === b.patientId && a.patientName === b.patientName;
  if (a.type === 'library_document' && b.type === 'library_document')
    return a.documentId === b.documentId;
  if (a.type === 'template' && b.type === 'template')
    return a.templateId === b.templateId;
  if (a.type === 'task' && b.type === 'task') return a.taskId === b.taskId;
  if (a.type === 'collection' && b.type === 'collection')
    return a.collectionId === b.collectionId;
  return false;
};

/**
 * A seeded turn's text has the linked-context preamble baked in —
 * `projectsComposerSubmitPrompts` prepends `serializeLinkedContext` to BOTH the
 * wire and display text. Strip it back off for the restore draft so a resend,
 * where the restored chip re-serializes the preamble, doesn't double it.
 */
export const stripLinkedContextPreamble = (
  text: string,
  linked: LinkedContextItem
): string => {
  const preamble = serializeLinkedContext([linked]).trim();
  return text.startsWith(preamble) ? text.slice(preamble.length).trimStart() : text;
};

/** Id-only linkage safe to persist — display fields are rehydrated on restore. */
export type PersistedLinkedContextRef =
  | { type: 'session'; sessionId: string }
  | { type: 'patient'; patientId: string }
  | { type: 'library_document'; documentId: string }
  | { type: 'template'; templateId: string };

export const toPersistedLinkedContextRef = (
  item: LinkedContextItem | null | undefined
): PersistedLinkedContextRef | null => {
  if (!item) return null;
  if (item.type === 'session') return { type: 'session', sessionId: item.sessionId };
  if (item.type === 'library_document')
    return { type: 'library_document', documentId: item.documentId };
  if (item.type === 'template') return { type: 'template', templateId: item.templateId };
  // Only `@` tokens name a task or a collection, and a token persists in the draft itself.
  if (item.type === 'task' || item.type === 'collection') return null;
  if (item.patientId) return { type: 'patient', patientId: item.patientId };
  return null;
};

/**
 * Resolve a persisted ref back to a live item. The ref is read straight out of
 * `sessionStorage`, so it may have been written by a DIFFERENT build: a ref
 * kind this one does not know must be DROPPED, never fall through to another
 * branch. A `{type:'template'}` ref reaching an id-blind patient lookup would
 * match the first profile-less patient (`undefined === undefined`) and turn a
 * template into a broken patient chip.
 */
export const resolveLinkedContextRef = (
  ref: PersistedLinkedContextRef,
  items: readonly LinkedContextItem[]
): LinkedContextItem | null => {
  switch (ref.type) {
    case 'session':
      return (
        items.find(
          (item) => item.type === 'session' && item.sessionId === ref.sessionId
        ) ?? null
      );
    case 'library_document':
      return (
        items.find(
          (item) => item.type === 'library_document' && item.documentId === ref.documentId
        ) ?? null
      );
    case 'template':
      return (
        items.find(
          (item) => item.type === 'template' && item.templateId === ref.templateId
        ) ?? null
      );
    case 'patient':
      // `toPersistedLinkedContextRef` only ever writes a patient ref WITH an
      // id, so an id-less one is malformed — matching on `undefined` would
      // silently attach whichever patient happens to lack a profile.
      return ref.patientId
        ? (items.find(
            (item) => item.type === 'patient' && item.patientId === ref.patientId
          ) ?? null)
        : null;
    default:
      return null;
  }
};
